import { featureFlagsService } from './featureFlagsService.js';
import { getTenantIdFromHostname, getCurrentTenantId, initializeHostMapping, normalizeHostname } from './hostMappingService.js';

export const TENANT_STORAGE_KEY = 'tenantId';
export const CONFIG_STORAGE_KEY = 'tenantConfig';

let isInitializing = false;
let initializationPromise = null;
let hostMappingInitialized = false;

const fetchFirstJson = async (urls) => {
  if (!urls.length) return null;
  try {
    return await Promise.any(
      urls.map(async (url) => {
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        });
        if (!response.ok) throw new Error(`${response.status}`);
        return response.json();
      })
    );
  } catch {
    return null;
  }
};

const getAppHostPrefix = () => {
  // Heuristic: in staging our endpoints/buckets typically include "stg-"
  const hint = `${process.env.REACT_APP_S3_BASE_URL || ''} ${process.env.REACT_APP_PARTICIPANTS_URL || ''}`.toLowerCase();
  return hint.includes('stg-') ? 'stg-app' : 'app';
};

/**
 * Carrega config base do tenant a partir do projeto "tenant-assets" (por host)
 * Usado para configs por tenant, como TURNSTILE_SITE_KEY.
 */
const loadTenantAssetsConfig = async (tenantId) => {
  const hostname = window.location.hostname;
  const normalizedHost = normalizeHostname(hostname);

  // Em localhost, queremos simular o host "stg-app-<tenant>movaincentivodigital"
  const derivedHostKey = tenantId ? `${getAppHostPrefix()}-${tenantId}movaincentivodigital` : null;
  const keysToTry = [...new Set([derivedHostKey, normalizedHost, hostname].filter(Boolean))];

  const s3BaseUrl = process.env.REACT_APP_S3_BASE_URL;
  const urls = [];

  for (const key of keysToTry) {
    // Local dev: servido pelo webpack-dev-server (ver webpack.config.js)
    urls.push(`/tenant-assets/assets/${key}.json`);

    // Remote fallback (bucket)
    if (s3BaseUrl) {
      urls.push(`${s3BaseUrl}/${key}.json`);
      urls.push(`${s3BaseUrl}/assets/${key}.json`);
    }
  }

  return await fetchFirstJson(urls);
};

export const getTenantId = () => {
  return sessionStorage.getItem(TENANT_STORAGE_KEY);
};

export const getTenantConfig = () => {
  const config = sessionStorage.getItem(CONFIG_STORAGE_KEY);
  return config ? JSON.parse(config) : null;
};

/**
 * Obtém o TENANT_ID baseado no hostname usando o mapeamento dinâmico
 * @returns {Promise<string|null>} Tenant ID ou null se não encontrado
 */
const getTenantIdFromHost = async () => {
  // Garante que o mapeamento foi inicializado
  if (!hostMappingInitialized) {
    await initializeHostMapping();
    hostMappingInitialized = true;
  }
  
  // Tenta obter do cache síncrono primeiro (mais rápido)
  const cachedTenantId = getCurrentTenantId();
  if (cachedTenantId) {
    return cachedTenantId;
  }
  
  // Se não encontrou no cache, busca dinamicamente
  const hostname = window.location.hostname;
  return await getTenantIdFromHostname(hostname);
};

/**
 * Carrega a configuração do tenant APENAS do CMS
 * Não usa mais o arquivo hosts/localhost.json
 */
export const loadTenantConfig = async () => {
  try {
    // Inicializa o mapeamento de hosts dinamicamente
    if (!hostMappingInitialized) {
      await initializeHostMapping();
      hostMappingInitialized = true;
    }
    
    // Obtém TENANT_ID do mapeamento de hosts
    const tenantId = await getTenantIdFromHost();
    
    if (!tenantId) {
      throw new Error(`Tenant ID not found for hostname: ${window.location.hostname}`);
    }
    
    // Salva o TENANT_ID primeiro para que o assetsService possa usá-lo
    sessionStorage.setItem(TENANT_STORAGE_KEY, tenantId);

    // Busca config base do tenant (tenant-assets) para mesclar com dados do CMS
    let tenantAssetsConfig = null;
    try {
      tenantAssetsConfig = await loadTenantAssetsConfig(tenantId);
    } catch (tenantAssetsError) {
      console.warn('Failed to load tenant-assets configuration:', tenantAssetsError);
    }

    // Obtém o idioma atual (pode ser do i18n ou localStorage)
    const currentLanguage = localStorage.getItem('i18nextLng') || 'pt-BR';
    const normalizedLang = currentLanguage === 'pt' ? 'pt-BR' : 
                          currentLanguage === 'en' ? 'en-US' : 
                          currentLanguage;
    
    // Busca FeatureFlags e outras configurações do CMS (pode falhar)
    let cmsConfig = null;
    try {
      cmsConfig = await featureFlagsService.getFeatureFlags(normalizedLang);
    } catch (featureFlagsError) {
      console.warn('Failed to load configuration from CMS:', featureFlagsError);
    }

    // Mescla tenant-assets (base) + CMS (override)
    const finalConfig = {
      TENANT_ID: tenantId,
      ...(tenantAssetsConfig?.FOLDER && { FOLDER: tenantAssetsConfig.FOLDER }),
      FEATURE_FLAGS: {
        ...(tenantAssetsConfig?.FEATURE_FLAGS || {}),
        ...(cmsConfig?.FEATURE_FLAGS || {}),
      },
      SIGNUP_TYPE: cmsConfig?.SIGNUP_TYPE || tenantAssetsConfig?.SIGNUP_TYPE || 'OPENED',
      CAMPAIGN_ID: cmsConfig?.CAMPAIGN_ID || tenantAssetsConfig?.CAMPAIGN_ID || null,
      CONFIGS: {
        ...(tenantAssetsConfig?.CONFIGS || {}),
        ...(cmsConfig?.CONFIGS || {}),
      },
      EXTRA_FIELDS: cmsConfig?.EXTRA_FIELDS || tenantAssetsConfig?.EXTRA_FIELDS || [],
      PROFILE_SELECT: cmsConfig?.PROFILE_SELECT || tenantAssetsConfig?.PROFILE_SELECT || null,
      ...(cmsConfig?.SEGMENT_DEFAULT && { SEGMENT_DEFAULT: cmsConfig.SEGMENT_DEFAULT }),
      ...(cmsConfig?.AUTHENTICATION_TYPE && { AUTHENTICATION_TYPE: cmsConfig.AUTHENTICATION_TYPE }),
      ...(cmsConfig?.WALLETS && Array.isArray(cmsConfig.WALLETS) && { WALLETS: cmsConfig.WALLETS }),
    };

    sessionStorage.setItem(TENANT_STORAGE_KEY, finalConfig.TENANT_ID);
    sessionStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(finalConfig));

    return finalConfig;
  } catch (error) {
    console.error('Failed to load tenant configuration:', error);
    throw new Error('Failed to load tenant configuration. Please try again later.');
  }
};

export const initializeTenant = async () => {
  if (isInitializing && initializationPromise) {
    return initializationPromise;
  }

  const cachedTenantId = getTenantId();
  if (cachedTenantId) {
    // Verifica se o tenantId em cache ainda corresponde ao hostname atual.
    // Se o mapeamento já foi inicializado, getCurrentTenantId() retorna o tenant
    // esperado para o host atual. Se divergir, descarta o cache para evitar
    // carregar o layout de outro tenant (ex: subway em vez de tigre).
    const expectedTenantId = getCurrentTenantId();
    if (!expectedTenantId || expectedTenantId === cachedTenantId) {
      return getTenantConfig();
    }
    // Host mudou (ou sessão anterior era de outro tenant) — limpa cache
    sessionStorage.removeItem(TENANT_STORAGE_KEY);
    sessionStorage.removeItem(CONFIG_STORAGE_KEY);
    try {
      sessionStorage.removeItem('hostTenantMapping');
    } catch (e) {
      // ignora
    }
  }

  isInitializing = true;
  initializationPromise = loadTenantConfig();
  
  try {
    const result = await initializationPromise;
    return result;
  } catch (error) {
    throw error;
  } finally {
    isInitializing = false;
    initializationPromise = null;
  }
};

export const migrateToSessionStorage = () => {
  const localTenantId = localStorage.getItem(TENANT_STORAGE_KEY);
  const localTenantConfig = localStorage.getItem(CONFIG_STORAGE_KEY);
  
  if (localTenantId && !sessionStorage.getItem(TENANT_STORAGE_KEY)) {
    sessionStorage.setItem(TENANT_STORAGE_KEY, localTenantId);
    localStorage.removeItem(TENANT_STORAGE_KEY);
  }
  
  if (localTenantConfig && !sessionStorage.getItem(CONFIG_STORAGE_KEY)) {
    sessionStorage.setItem(CONFIG_STORAGE_KEY, localTenantConfig);
    localStorage.removeItem(CONFIG_STORAGE_KEY);
  }
};
